/**
 * AFFILIATE FIRST TOUCH (job #433, Tom 2026-10-02).
 *
 * An affiliate earns only on the people THEY bring, first touch, and an SSi-paid ad earns
 * them nothing. This file is the browser's half of that:
 *
 *   - `/r/<CODE>` is the affiliate's link. It is rewritten in main.js to `/?ref=<CODE>`, the
 *     same way `/learn/<course>` is, and `ref` is already a harmless key to the first-load
 *     picker (platform/pickerFirst).
 *   - `captureAffiliateTouch` runs once at boot and remembers the FIRST touch on this device:
 *     an affiliate code, or a paid ad (gclid / fbclid / msclkid, or utm_medium cpc / ppc /
 *     paid / paid_social). Whichever came first stays; nothing later replaces it. A paid-ad
 *     first touch means no code is ever sent — the browser denying itself, which needs no
 *     trust.
 *   - `claimAffiliateTouch` sends an unclaimed affiliate touch to /api/affiliate/attribute
 *     once there is a session. The server is the authority: it writes the durable attribution
 *     (first touch per account, refused after any purchase) and this file only forgets the
 *     touch once the server has answered for good.
 *   - `affiliateCodeForCheckout` is what lib/paddle.ts puts on every checkout's customData,
 *     for audit. The webhook never trusts it for money.
 */

export const AFFILIATE_TOUCH_KEY = 'ssi-affiliate-touch'

/**
 * A touch counts for this long (job #434): an affiliate link clicked 90 days before sign-up
 * still brought the learner; one clicked a year ago did not. An expired, unclaimed touch is
 * forgotten, so a fresher one (an affiliate's, or a paid ad's) can take its place.
 */
export const AFFILIATE_TOUCH_WINDOW_DAYS = 90

function expired(t: AffiliateTouch, now: number): boolean {
  if (t.kind === 'affiliate' && t.claimed) return false
  const at = Date.parse(t.at)
  return !Number.isFinite(at) || now - at > AFFILIATE_TOUCH_WINDOW_DAYS * 24 * 3600 * 1000
}

const CODE_RE = /^[A-Z0-9]{3,24}$/
const R_PATH = /^\/r(?:\/([^/]*))?\/?$/
const PAID_MEDIUM = /^(cpc|ppc|paid|paid[_-]?social|paidsearch|display)$/i
const AD_CLICK_KEYS = ['gclid', 'fbclid', 'msclkid', 'gbraid', 'wbraid', 'ttclid']

export type AffiliateTouch =
  | { kind: 'affiliate'; code: string; source: 'link' | 'code'; at: string; claimed?: boolean }
  | { kind: 'paid_ad'; at: string }

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function storage(): Store | null {
  try { return typeof localStorage !== 'undefined' ? localStorage : null } catch { return null }
}

export function normaliseCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const c = raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
  return CODE_RE.test(c) ? c : null
}

export function readTouch(store: Store | null = storage()): AffiliateTouch | null {
  try {
    const raw = store?.getItem(AFFILIATE_TOUCH_KEY)
    return raw ? (JSON.parse(raw) as AffiliateTouch) : null
  } catch {
    return null
  }
}

function writeTouch(t: AffiliateTouch, store: Store | null = storage()): void {
  try { store?.setItem(AFFILIATE_TOUCH_KEY, JSON.stringify(t)) } catch { /* private mode */ }
}

/** `/r/<CODE>` → `/?ref=<CODE>` (other query keys ride along); null when not an affiliate link. */
export function affiliateLinkTarget(pathname: string, search: string): string | null {
  const m = R_PATH.exec(pathname || '')
  if (!m) return null
  let code: string | null = null
  try { code = normaliseCode(decodeURIComponent(m[1] || '')) } catch { code = null }
  if (!code) return null // bare /r is the type-a-code page, a real route
  const params = new URLSearchParams(search || '')
  params.set('ref', code)
  return `/?${params.toString()}`
}

export function rewriteAffiliateLink(loc: Location = window.location, hist: History = window.history): boolean {
  const target = affiliateLinkTarget(loc.pathname, loc.search)
  if (!target) return false
  hist.replaceState(hist.state, '', target + (loc.hash || ''))
  return true
}

/** What this visit says about where the visitor came from, if anything. */
export function touchFromSearch(search: string, now = new Date()): AffiliateTouch | null {
  const params = new URLSearchParams(search || '')
  const at = now.toISOString()
  if (AD_CLICK_KEYS.some((k) => params.has(k)) || PAID_MEDIUM.test(params.get('utm_medium') || '')) {
    return { kind: 'paid_ad', at }
  }
  const code = normaliseCode(params.get('ref'))
  return code ? { kind: 'affiliate', code, source: 'link', at } : null
}

/** First touch wins: record this one only if the device has none. Returns what is stored. */
export function recordTouch(t: AffiliateTouch | null, store: Store | null = storage(), now = Date.now()): AffiliateTouch | null {
  let existing = readTouch(store)
  if (existing && expired(existing, now)) {
    try { store?.removeItem(AFFILIATE_TOUCH_KEY) } catch { /* private mode */ }
    existing = null
  }
  if (existing || !t) return existing
  writeTouch(t, store)
  return t
}

/** Boot: read the address once. */
export function captureAffiliateTouch(search: string = typeof location !== 'undefined' ? location.search : ''): AffiliateTouch | null {
  return recordTouch(touchFromSearch(search))
}

/** A code typed on /r. Same first-touch rule as a link. */
export function recordTypedCode(raw: string): AffiliateTouch | null {
  const code = normaliseCode(raw)
  if (!code) return null
  return recordTouch({ kind: 'affiliate', code, source: 'code', at: new Date().toISOString() })
}

export function affiliateCodeForCheckout(store: Store | null = storage()): string | null {
  const t = readTouch(store)
  return t && t.kind === 'affiliate' && !expired(t, Date.now()) ? t.code : null
}

let inFlight: Promise<void> | null = null

/**
 * Send an unclaimed affiliate touch to the server. Idempotent, best effort, never throws.
 * Kept for another try when the account row is not there yet (409) or the network failed;
 * marked claimed on every answer that is final (attributed, already attributed, already a
 * customer, own code); dropped when the code is unknown.
 */
export function claimAffiliateTouch(accessToken: string | null | undefined, fetchImpl: typeof fetch = fetch, store: Store | null = storage()): Promise<void> {
  if (!accessToken) return Promise.resolve()
  const t = readTouch(store)
  if (!t || t.kind !== 'affiliate' || t.claimed) return Promise.resolve()
  if (expired(t, Date.now())) {
    try { store?.removeItem(AFFILIATE_TOUCH_KEY) } catch { /* private mode */ }
    return Promise.resolve()
  }
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const res = await fetchImpl('/api/affiliate/attribute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ code: t.code, source: t.source }),
      })
      if (res.status === 404 || res.status === 400) {
        store?.removeItem(AFFILIATE_TOUCH_KEY)
      } else if (res.ok) {
        writeTouch({ ...t, claimed: true }, store)
      }
    } catch {
      /* offline: try again on the next sign-in */
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}
